import { useCallback, useMemo } from 'react';

import { useQueryClient, useQuery } from '@tanstack/react-query';
import { wizardFormKeys } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/hooks/queryKeys';
import { mapHierarchy } from 'pages/home/ribbon/components/sections/product/components/product-upload-wizard/utils/wizardProduct';

import { getWizardFormItems } from 'services/product';

export const fetchWizardFormItems = (params) => getWizardFormItems(params);

export const useGetWizardFormItems = () => {
  const queryClient = useQueryClient();

  const getWizardFormItems = useCallback(
    (params) => {
      return queryClient.fetchQuery({
        queryKey: wizardFormKeys.wizardItems(params?.productIds),
        queryFn: () => fetchWizardFormItems(params),
        staleTime: 0,
      });
    },
    [queryClient]
  );

  return { getWizardFormItems };
};

/**
 * Fetches wizardFormItems for a single product by ID and derives
 * the mapped hierarchy list ready for wizard state.
 * Used to hydrate hierarchies when opening from a pending form.
 */
export const useGetProductHierarchies = ({ productId, enabled }) => {
  const { isFetching, data } = useQuery({
    queryKey: wizardFormKeys.wizardItems([productId]),
    queryFn: () => fetchWizardFormItems({ productIds: [productId] }),
    enabled: enabled && !!productId,
  });

  const freshHierarchies = useMemo(() => {
    if (!data) return null;
    const wizardFormItems = data?.data?.wizardFormItems ?? [];
    if (!wizardFormItems.length) return null;
    return (wizardFormItems[0]?.hierarchies ?? []).map(mapHierarchy);
  }, [data]);

  return { isFetching, freshHierarchies };
};

export default useGetWizardFormItems;
